import React, { useState, useRef, useMemo } from 'react';
import Draggable from 'react-draggable';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, Tooltip, TablePagination, TextField, TableSortLabel, Box, Typography
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { beautifyNumber } from '../../../../Utils/FormatNumber';
import { isAssetSold, formatAssetDescription } from '../../../../Utils/assetSaleDisplayUtils';
import {
    getSoldAssetsForDisplay,
    buildAssetGroupMap,
    buildSoldAssetRelationMaps,
    getTopLevelSoldAssets,
    filterTopLevelSoldWithChildSearch,
    SOLD_ASSET_ROOT_GROUP_BG,
    SOLD_ASSET_CHILD_ROW_BG,
    SOLD_ASSET_ROW_HOVER_BG,
} from '../../../../Utils/soldAssetsTableUtils';

const BoundsWrapper = styled('div')({
    position: 'fixed',
    inset: 0,
    zIndex: 200,
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const ModalOverlay = styled('div')({
    width: 'min(96vw, 1100px)',
    maxHeight: 'min(88vh, 720px)',
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.25)',
    border: '1px solid #304FFF',
    overflow: 'hidden',
});

function descendingComparator(a, b, orderBy) {
    if (a[orderBy] < b[orderBy]) return 1;
    if (a[orderBy] > b[orderBy]) return -1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilized = array.map((el, index) => [el, index]);
    stabilized.sort((a, b) => {
        const ord = comparator(a[0], b[0]);
        if (ord !== 0) return ord;
        return a[1] - b[1];
    });
    return stabilized.map((el) => el[0]);
}

const formatDateShort = (d) =>
    !d ? '-' : new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });

const columnsDef = (t) => [
    { id: 'assetNumber', label: t('AssetNumber') || 'מספר נכס' },
    { id: 'name', label: t('AssetName') || 'שם נכס' },
    { id: 'purchaseDate', label: t('PurchaseDate') || 'תאריך רכישה' },
    { id: 'originalPrice', label: t('OriginalPrice') || 'מחיר מקורי' },
    { id: 'sellDate', label: t('SellDate') || 'תאריך מכירה' },
    { id: 'sellAmount', label: t('SellAmount') || 'סכום מכירה' },
    { id: 'assetGroupId', label: t('AssetGroup') || 'קבוצה' },
    { id: 'description', label: t('Description') || 'הערות' },
];

/** Child rows: arrow + name in Asset Name; asset number stays in its own column. */
function formatChildAssetNameLabel(asset, isRtl) {
    const marker = isRtl ? '↳' : '↲';
    const name = (asset?.name || '').trim();
    return name ? `${marker} ${name}` : marker;
}

function SoldAssetTableCells({
    asset,
    groupMap,
    t,
    isChildRow = false,
    isRtl = true,
}) {
    const isLinkedChildSale = Boolean(asset.soldViaAssetGroup) || isChildRow;
    const isMissing = !isLinkedChildSale && (!asset.sellAmount || asset.sellAmount === 0);
    const displayDescription = formatAssetDescription(asset.description, asset.sellDate, isAssetSold(asset));

    return (
        <>
            <TableCell
                align="center"
                sx={isChildRow ? { color: '#666', fontSize: 13 } : undefined}
            >
                {asset.assetNumber ?? '-'}
            </TableCell>
            <TableCell
                align={isChildRow ? 'right' : 'center'}
                sx={
                    isChildRow
                        ? {
                              color: '#666',
                              fontSize: 13,
                              whiteSpace: 'nowrap',
                          }
                        : undefined
                }
            >
                {isChildRow ? formatChildAssetNameLabel(asset, isRtl) : (asset.name ?? '-')}
            </TableCell>
            <TableCell align="center" sx={isChildRow ? { color: '#666' } : undefined}>
                {formatDateShort(asset.purchaseDate)}
            </TableCell>
            <TableCell align="center" sx={isChildRow ? { color: '#666' } : undefined}>
                {beautifyNumber(asset.originalPrice, 0)}
            </TableCell>
            <TableCell align="center" sx={isChildRow ? { color: '#666' } : undefined}>
                {formatDateShort(asset.sellDate)}
            </TableCell>
            <TableCell align="center" sx={isChildRow ? { color: '#666' } : undefined}>
                {isLinkedChildSale ? (
                    '—'
                ) : isMissing ? (
                    <Tooltip title={t('MissingSellAmount')} arrow>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 0.5,
                                color: '#d32f2f',
                            }}
                        >
                            <WarningAmberIcon sx={{ fontSize: 16 }} />
                            <span>{t('Missing')}</span>
                        </Box>
                    </Tooltip>
                ) : (
                    beautifyNumber(asset.sellAmount, 0)
                )}
            </TableCell>
            <TableCell align="center" sx={isChildRow ? { color: '#666' } : undefined}>
                {groupMap[asset.assetGroupId] || '-'}
            </TableCell>
            <TableCell align="center" sx={isChildRow ? { color: '#666', fontSize: 12 } : undefined}>
                {displayDescription || '-'}
            </TableCell>
        </>
    );
}

const CapitalGainSoldAssetsModal = ({
    open = false,
    onClose = () => {},
    onSelectAsset = () => {},
    assets = [],
    assetGroups = [],
    assetRelations = [],
}) => {
    const { t, i18n } = useTranslation();
    const nodeRef = useRef(null);
    const boundsRef = useRef(null);
    const isRtl = i18n.dir() === 'rtl';

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('sellDate');
    const [searchTerm, setSearchTerm] = useState('');

    const groupMap = useMemo(() => buildAssetGroupMap(assetGroups), [assetGroups]);
    const columns = useMemo(() => columnsDef(t), [t]);

    const soldAssets = useMemo(
        () => getSoldAssetsForDisplay(assets, assetRelations),
        [assets, assetRelations]
    );

    const { childAssetIdSet, rootToChildren } = useMemo(
        () => buildSoldAssetRelationMaps(soldAssets, assetRelations),
        [soldAssets, assetRelations]
    );

    const topLevelSoldAssets = useMemo(
        () => getTopLevelSoldAssets(soldAssets, childAssetIdSet),
        [soldAssets, childAssetIdSet]
    );

    const missingAmountCount = useMemo(
        () =>
            soldAssets.filter((a) => !a.soldViaAssetGroup && (!a.sellAmount || a.sellAmount === 0)).length,
        [soldAssets]
    );

    const filtered = useMemo(
        () => filterTopLevelSoldWithChildSearch(topLevelSoldAssets, rootToChildren, searchTerm, groupMap),
        [topLevelSoldAssets, rootToChildren, searchTerm, groupMap]
    );

    const sorted = useMemo(() => {
        if (!orderBy) return filtered;
        return stableSort(filtered, getComparator(order, orderBy));
    }, [filtered, order, orderBy]);

    const paginated = useMemo(
        () => sorted.slice(page * rowsPerPage, (page + 1) * rowsPerPage),
        [sorted, page, rowsPerPage]
    );

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    if (!open) return null;

    return (
        <BoundsWrapper ref={boundsRef}>
            <Draggable handle=".capital-gain-sold-handle" nodeRef={nodeRef} bounds="parent">
                <ModalOverlay ref={nodeRef}>
                    <div
                        className="capital-gain-sold-handle"
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            cursor: 'move',
                            marginBottom: 8,
                        }}
                    >
                        <Tooltip title={t('Close')}>
                            <IconButton onClick={onClose}>
                                <CancelIcon style={{ color: '#304FFF' }} />
                            </IconButton>
                        </Tooltip>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#304FFF' }}>
                            {t('CapitalGainSelectSoldAsset') || 'הפקת טופס רווח הון — בחר נכס שנמכר'}
                        </Typography>
                        <div style={{ width: 40 }} />
                    </div>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 2 }}>
                        <TextField
                            placeholder={t('Search') || 'חיפוש'}
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setPage(0);
                            }}
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                sx: {
                                    height: '40px',
                                    width: '250px',
                                    padding: '0 10px',
                                    borderRadius: '8px',
                                    background: '#F5F5F6',
                                    border: '1px solid transparent',
                                    '&:focus-within': { border: '2px solid #304FFF' },
                                },
                            }}
                            inputProps={{ style: { textAlign: 'right', direction: 'rtl' } }}
                        />
                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                            <Typography variant="body2">
                                {t('TotalSold') || 'סה"כ נמכרו'}: <strong>{soldAssets.length}</strong>
                            </Typography>
                            {missingAmountCount > 0 && (
                                <Typography
                                    variant="body2"
                                    sx={{ color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 0.5 }}
                                >
                                    <WarningAmberIcon sx={{ fontSize: 16 }} />
                                    {t('MissingSellAmount')}: <strong>{missingAmountCount}</strong>
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <TableContainer component={Paper} sx={{ flex: 1, minHeight: 0, maxHeight: '50vh' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {columns.map((col) => (
                                        <TableCell key={col.id} align="center" sx={{ fontWeight: 700 }}>
                                            <TableSortLabel
                                                active={orderBy === col.id}
                                                direction={orderBy === col.id ? order : 'asc'}
                                                onClick={() => handleRequestSort(col.id)}
                                            >
                                                {col.label}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginated.map((asset) => {
                                    const children = rootToChildren[asset.id] || [];
                                    const hasChildren = children.length > 0;
                                    const isMissing =
                                        !asset.soldViaAssetGroup &&
                                        (!asset.sellAmount || asset.sellAmount === 0);

                                    const rootBg = isMissing
                                        ? 'rgba(211, 47, 47, 0.08)'
                                        : hasChildren
                                          ? SOLD_ASSET_ROOT_GROUP_BG
                                          : '#fff';

                                    return (
                                        <React.Fragment key={asset.id}>
                                            <TableRow
                                                hover={!isMissing}
                                                onClick={() => onSelectAsset(asset)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    backgroundColor: rootBg,
                                                    ...(hasChildren && {
                                                        borderInlineStart: '3px solid #304FFF',
                                                    }),
                                                    '&:hover': {
                                                        backgroundColor: isMissing
                                                            ? 'rgba(211, 47, 47, 0.14)'
                                                            : SOLD_ASSET_ROW_HOVER_BG,
                                                    },
                                                }}
                                            >
                                                <SoldAssetTableCells
                                                    asset={asset}
                                                    groupMap={groupMap}
                                                    t={t}
                                                    isChildRow={false}
                                                    isRtl={isRtl}
                                                />
                                            </TableRow>
                                            {children.map((child) => (
                                                <TableRow
                                                    key={`child-${child.id}`}
                                                    sx={{
                                                        backgroundColor: SOLD_ASSET_CHILD_ROW_BG,
                                                        cursor: 'default',
                                                        borderInlineStart: '3px solid rgba(48, 79, 255, 0.2)',
                                                        '&:hover': {
                                                            backgroundColor: SOLD_ASSET_CHILD_ROW_BG,
                                                        },
                                                    }}
                                                >
                                                    <SoldAssetTableCells
                                                        asset={child}
                                                        groupMap={groupMap}
                                                        t={t}
                                                        isChildRow
                                                        isRtl={isRtl}
                                                    />
                                                </TableRow>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                                {paginated.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 3, color: '#999' }}>
                                            {t('NoSoldAssets') || 'אין נכסים שנמכרו'}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        count={sorted.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        labelRowsPerPage={t('RowsPerPage')}
                    />
                </ModalOverlay>
            </Draggable>
        </BoundsWrapper>
    );
};

export default CapitalGainSoldAssetsModal;
